import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, TemplateRef } from "@angular/core";
import { MAT_MENU_DEFAULT_OPTIONS } from "@angular/material/menu";
import { NgxPermissionsService } from "ngx-permissions";
import { take, tap } from "rxjs";
export type GenericHeaderMenuMethod<T> = () => T;

/**
 * @interface HeaderMenuOption
 * @description menu option used in 3 dot menu
 * @param {string | GenericHeaderMenuMethod} title the text displayed
 * @param {GenericHeaderMenuMethod<void>} method the method to call when clicked
 * @param {boolean | GenericHeaderMenuMethod<boolean>} enabled determines whether or not item is clickable
 * @param {string[]}permissions array of permissions needed to view item
 * @param {string}helpText optional text to show to the right of the option
 */
export interface HeaderMenuOption {
    title: string | GenericHeaderMenuMethod<string>;
    method: GenericHeaderMenuMethod<void>;
    isEnabled: boolean | GenericHeaderMenuMethod<boolean>;
    permissions: string[];
    helpText?: string | GenericHeaderMenuMethod<string>;
}

@Component({
    selector: "app-generic-header",
    templateUrl: "./generic-header.component.html",
    styleUrls: ["./generic-header.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: MAT_MENU_DEFAULT_OPTIONS,
            useValue: {
                xPosition: "before",
                overlayPanelClass: "header-menu-panel-overlay",
            },
        },
    ],
})
export class GenericHeaderComponent implements OnInit {
    @Input() contentTemplate: TemplateRef<any>;
    @Input() backgroundColor = "#1a1a1a";
    @Input() menuOptions: HeaderMenuOption[] = [];

    public isMenuOpen = false;
    public shouldShowThreeDotMenu = false;

    constructor(private readonly cdr: ChangeDetectorRef, private readonly permissionsService: NgxPermissionsService) {}

    ngOnInit(): void {
        // verify user has permission to view at least one option before showing menu
        if (this.menuOptions.length) {
            this.permissionsService.permissions$
                .pipe(
                    take(1),
                    tap(userPermissions => {
                        for (const option of this.menuOptions) {
                            if (
                                !option.permissions.length ||
                                option.permissions.some(permission => Object.keys(userPermissions).includes(permission))
                            ) {
                                this.shouldShowThreeDotMenu = true;
                                break;
                            }
                        }
                    })
                )
                .subscribe();
        }
    }

    public getHeaderItemValue(item: HeaderMenuOption["isEnabled" | "title" | "helpText"]): boolean | string {
        if (typeof item === "function") {
            return item();
        }
        return item;
    }

    // prevents material menu from closing on disabled button click
    public onOptionWrapperClick(event: MouseEvent, isEnabled: HeaderMenuOption["isEnabled"]): void {
        if (!this.getHeaderItemValue(isEnabled)) {
            event.stopPropagation();
        }
    }

    public triggerChangeDetection(): void {
        this.cdr.detectChanges();
    }
}
